import type { CommonImageItemType } from '../types';

/** 与接口、制品 installScript 一致的环境变量项 */
export interface ContainerStartupEnvEntry {
  name: string;
  value: string;
}

export interface ContainerStartupPayload {
  command: string[];
  args: string[];
  env: ContainerStartupEnvEntry[];
}

const emptyPayload: ContainerStartupPayload = { command: [], args: [], env: [] };

/**
 * 解析容器制品 installScript（JSON 字符串），得到启动命令、参数、环境变量。
 * env 兼容制品表单曾使用的 key 字段。
 */
export function parseContainerInstallScript(installScript: string | undefined | null): ContainerStartupPayload {
  if (!installScript || typeof installScript !== 'string') {
    return { ...emptyPayload };
  }
  try {
    const parsed = JSON.parse(installScript) as Record<string, unknown>;
    const command = Array.isArray(parsed.command) ? parsed.command.map((s) => String(s)) : [];
    const args = Array.isArray(parsed.args) ? parsed.args.map((s) => String(s)) : [];
    let env: ContainerStartupEnvEntry[] = [];
    if (Array.isArray(parsed.env)) {
      env = parsed.env.map((e: Record<string, unknown>) => ({
        name: String(e?.name ?? e?.key ?? ''),
        value: String(e?.value ?? ''),
      }));
    }
    return { command, args, env };
  } catch {
    return { ...emptyPayload };
  }
}

/** 表单或展示用的逗号分隔字符串 */
export function startupArrayToCommaText(arr: unknown): string {
  if (!Array.isArray(arr)) return '';
  return arr.map((s) => String(s)).join(', ');
}

/** 逗号分隔输入转数组（与制品中心「启动命令」填写习惯一致） */
export function commaTextToStartupArray(text: unknown): string[] {
  if (Array.isArray(text)) return text.map((s) => String(s).trim()).filter(Boolean);
  if (typeof text !== 'string') return [];
  return text
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 表单展示：启动命令数组转空格分隔字符串（MaaS 节点弹窗） */
export function startupArrayToSpaceText(arr: unknown): string {
  if (!Array.isArray(arr)) return typeof arr === 'string' ? arr : '';
  return arr.map((s) => String(s)).join(' ');
}

/** 空格分隔输入转数组（MaaS 节点弹窗提交/回填） */
export function spaceTextToStartupArray(text: unknown): string[] {
  if (Array.isArray(text)) return text.map((s) => String(s).trim()).filter(Boolean);
  if (typeof text !== 'string') return [];
  const trimmed = text.trim();
  if (!trimmed) return [];
  return trimmed.split(/\s+/);
}

/** 从镜像列表项得到启动项默认值（优先 installScript） */
export function getContainerStartupDefaultsFromImageItem(
  item: CommonImageItemType | null | undefined,
  fallback?: Partial<ContainerStartupPayload>,
): ContainerStartupPayload {
  const fromScript = parseContainerInstallScript(item?.installScript);
  return {
    command: fromScript.command.length ? fromScript.command : (fallback?.command ?? []),
    args: fromScript.args.length ? fromScript.args : (fallback?.args ?? []),
    env: fromScript.env.length ? fromScript.env : (fallback?.env ?? []),
  };
}
